import { cityLabel } from '@rekindle/shared/types/homeCity';
import { GrantBonusDialog } from './users/GrantBonusDialog';
import { PartnerLink } from './users/UserAccessFields';
import { UserAICostCard } from './users/UserAICostCard';
import { useAdminText } from './shared/useAdminText';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GeneratePhotoExport } from './PhotoArchives';
/**
 * User Show Component
 * Displays detailed user information with plan management
 */

import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Show,
  SimpleShowLayout,
  useRecordContext,
  TopToolbar,
  ListButton,
} from 'react-admin';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Grid,
  Divider,
} from '@mui/material';

import { UserSubscriptionCard } from './subscriptions/UserSubscriptionCard';

/**
 * User details section with plan management
 */
function UserDetails() {
  const record = useRecordContext();
  const t = useAdminText();
  const [bonusDialogOpen, setBonusDialogOpen] = useState(false);

  if (!record) return null;

  const movieNight = record.movieNight;
  const movieNightFeedStatus = !movieNight?.feed
    ? 'Not created'
    : movieNight.feed.isGenerating && movieNight.feed.hasPendingSettings
      ? 'Generating replacement'
      : movieNight.feed.isGenerating
        ? 'Generating'
        : movieNight.feed.hasPendingSettings
          ? 'Replacement pending'
          : 'Ready';

  return (
    <>
      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid size={{ xs: 12, md: 6 }} component="div">
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Email
                </Typography>
                <Typography>{record.email}</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Name
                </Typography>
                <Typography>
                  {record.name || 'Not set'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">{t("partner", "Partner")}</Typography>
                <Box><PartnerLink partner={record.partner} /></Box>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  User ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {record.id}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} component="div">
          <UserSubscriptionCard onGrantBonus={() => setBonusDialogOpen(true)} />
        </Grid>

        {/* Activity Info */}
        <Grid size={{ xs: 12, md: 6 }} component="div">
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Activity
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box mb={2}><Chip label={record.hasProfile ? t("profileComplete", "Profile completed") : t("profileIncomplete", "Profile incomplete")} color={record.hasProfile ? "success" : "default"} size="small" /></Box>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">Timezone</Typography>
                <Typography data-testid="admin-user-city">{record.homeCity ? cityLabel(record.homeCity) : 'City not set'}</Typography>
                <Typography data-testid="admin-user-timezone">{record.timeZone ?? 'Not set (onboarding pending)'}</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Joined
                </Typography>
                <Typography>
                  {record.createdAt
                    ? new Date(record.createdAt).toLocaleString()
                    : 'Unknown'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Last Updated
                </Typography>
                <Typography>
                  {record.updatedAt
                    ? new Date(record.updatedAt).toLocaleString()
                    : 'Unknown'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="textSecondary">
                  Cards Swiped
                </Typography>
                <Typography>
                  {record.cardSwapCount ?? 0}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} component="div">
          <UserAICostCard key={record.id} userId={String(record.id)} />
        </Grid>

        <Grid size={{ xs: 12 }} component="div">
          <Accordion data-testid="memories-admin-section"><AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="h6">Our Memories</Typography></AccordionSummary><AccordionDetails>
            <Typography>Retained photos: {record.memories?.retained ?? 0}</Typography>
            <Typography>Awaiting export and removal: {record.memories?.retiring ?? 0}</Typography>
            <Typography>Latest photo: {record.memories?.latest ? `${record.memories.latest.day} · added ${new Date(record.memories.latest.addedAt).toLocaleString()}` : 'No photos yet'}</Typography>
            <Typography>Current usage: {record.memories?.capacity.used ?? 0} / {record.memories?.capacity.unlimited ? 'Unlimited' : record.memories?.capacity.limit ?? '—'}</Typography>
            <Typography>Extra photo slots: {record.memories?.capacity.bonus ?? 0}</Typography>
            <Typography variant="caption">Includes admin bonuses and photo spaces purchased with hearts.</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Metadata only. Private photos and captions are not available in administration.</Typography>
            <Button component={RouterLink} to={`/users/${record.id}/usage`}>View usage and bonuses</Button>
            <Button component={RouterLink} to={`/photo-exports?filter=${encodeURIComponent(JSON.stringify({ userId: record.id }))}`}>View photo archives</Button>
          </AccordionDetails></Accordion>
        </Grid>

        {/* Movie Night diagnostics */}
        <Grid size={{ xs: 12 }} component="div">
          <Accordion data-testid="movie-night-admin-section"><AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="h6">Movie Night</Typography></AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Read-only operational diagnostics. Countries, services, titles, and vote choices
                are intentionally not exposed.
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="textSecondary">
                    Profile
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={movieNight?.profileExists ? 'Created' : 'Not created'}
                      color={movieNight?.profileExists ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="textSecondary">
                    User service filter
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={movieNight?.serviceFilterReady ? 'Ready' : 'General feed'}
                      color={movieNight?.serviceFilterReady ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="textSecondary">
                    Shared filtering
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={movieNight?.feed?.filteringActive ? 'Active' : 'General feed'}
                      color={movieNight?.feed?.filteringActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="textSecondary">
                    Feed status
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={movieNightFeedStatus}
                      color={movieNight?.feed?.isGenerating ? 'warning' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Votes
                  </Typography>
                  <Typography>{movieNight?.voteCount ?? 0}</Typography>
                </Grid>

                <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Matches
                  </Typography>
                  <Typography>{movieNight?.matchCount ?? 0}</Typography>
                </Grid>

                <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Active cards
                  </Typography>
                  <Typography>{movieNight?.feed?.activeCandidateCount ?? 0}</Typography>
                </Grid>

                <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Unseen cards
                  </Typography>
                  <Typography>{movieNight?.feed?.unseenCount ?? 0}</Typography>
                </Grid>

                <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Generation
                  </Typography>
                  <Typography>{movieNight?.feed?.activeGeneration ?? 0}</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 8, md: 4 }}>
                  <Typography variant="caption" color="textSecondary">
                    Feed last updated
                  </Typography>
                  <Typography>
                    {movieNight?.feed?.updatedAt
                      ? new Date(movieNight.feed.updatedAt).toLocaleString()
                      : 'Never'}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {bonusDialogOpen && <GrantBonusDialog
        key={record.id}
        onClose={() => setBonusDialogOpen(false)}
        userId={String(record.id)}
      />}
    </>
  );
}

/**
 * Show actions toolbar
 */
function ShowActions() {
  return (
    <TopToolbar>
      <ListButton />
      <GeneratePhotoExport />
    </TopToolbar>
  );
}

export function UserShow() {
  return (
    <Show actions={<ShowActions />}>
      <SimpleShowLayout>
        <UserDetails />
      </SimpleShowLayout>
    </Show>
  );
}
